import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Copy, ExternalLink, Trash2, Edit,
  MousePointerClick, Monitor, Smartphone, Tablet, Globe,
  TrendingUp, Users, Calendar, Bot, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';

const API_URL = window.location.origin + '/api';
const DOMAIN = window.location.host;

const COLORS = ['#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const SOURCE_COLORS = {
  facebook: '#1877F2',
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  tiktok: '#69C9D0',
  reddit: '#FF4500',
  pinterest: '#E60023',
  telegram: '#26A5E4',
  whatsapp: '#25D366',
  google: '#4285F4',
  dogrudan: '#06B6D4',
  diger: '#8B5CF6',
};

const SOURCE_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter/X',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  reddit: 'Reddit',
  pinterest: 'Pinterest',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  google: 'Google',
  dogrudan: 'Doğrudan',
  diger: 'Diğer',
};

const LinkDetails = () => {
  const { linkId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ title: '', is_active: true });
  const [editLoading, setEditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('genel');

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/links/${linkId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
      setEditData({
        title: response.data?.link?.title || '',
        is_active: response.data?.link?.is_active !== false
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Analitik veriler yüklenemedi');
      navigate('/panel');
    }
    setLoading(false);
  }, [linkId, token, navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await axios.put(`${API_URL}/links/${linkId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link güncellendi');
      setEditDialogOpen(false);
      fetchAnalytics();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
    setEditLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Bu linki silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link silindi');
      navigate('/panel');
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopyalandı!');
  };

  const getShortUrl = (shortCode) => {
    return `https://${DOMAIN}/${shortCode}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!analytics || !analytics.link) return null;

  const { link, total_clicks, bot_clicks_filtered, devices, browsers, os_stats, countries, referrers, daily_clicks, daily_clicks_by_source, sources, source_totals } = analytics;

  const safeDevices = devices && typeof devices === 'object' ? devices : {};
  const safeBrowsers = browsers && typeof browsers === 'object' ? browsers : {};
  const safeCountries = countries && typeof countries === 'object' ? countries : {};
  const safeReferrers = referrers && typeof referrers === 'object' ? referrers : {};
  const safeOsStats = os_stats && typeof os_stats === 'object' ? os_stats : {};
  const safeDailyClicks = Array.isArray(daily_clicks) ? daily_clicks : [];
  const safeDailyBySource = Array.isArray(daily_clicks_by_source) ? daily_clicks_by_source : [];
  const safeSources = Array.isArray(sources) ? sources : [];
  const safeSourceTotals = source_totals && typeof source_totals === 'object' ? source_totals : {};

  const deviceData = Object.entries(safeDevices).map(([name, value]) => ({ name, value }));
  const browserData = Object.entries(safeBrowsers).map(([name, value]) => ({ name, value }));
  const countryData = Object.entries(safeCountries).map(([name, value]) => ({ name, value }));
  const referrerData = Object.entries(safeReferrers).slice(0, 10).map(([name, value]) => ({ 
    name: name === 'Doğrudan' ? 'Doğrudan' : (name || '').substring(0, 30), 
    value 
  }));

  const tabs = [
    { id: 'genel', label: 'Genel' },
    { id: 'gunluk', label: 'Günlük Tıklamalar' },
    { id: 'yonlendirmeler', label: 'Yönlendirmeler' },
  ];

  const CustomSourceTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-[#1C1C1E] border border-white/10 rounded-lg p-3 shadow-xl">
        <p className="text-slate-300 text-sm mb-2">
          {(() => { try { return new Date(label).toLocaleDateString('tr-TR'); } catch { return label; } })()}
        </p>
        {payload.filter(p => p.value > 0).map((p, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: p.color }} />
            <span className="text-slate-400">{SOURCE_LABELS[p.dataKey] || p.dataKey}:</span>
            <span className="text-white font-medium">{p.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderGeneralTab = () => (
    <>
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="chart-container col-span-full">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6">Günlük Tıklamalar (Son 30 Gün)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={safeDailyClicks}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="#64748B"
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => {
                  try { return new Date(value).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }); } catch { return value; }
                }}
              />
              <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F8FAFC' }}
                labelFormatter={(value) => { try { return new Date(value).toLocaleDateString('tr-TR'); } catch { return value; } }}
              />
              <Line type="monotone" dataKey="clicks" stroke="#06B6D4" strokeWidth={2} dot={{ fill: '#06B6D4', r: 3 }} activeDot={{ r: 5, fill: '#22D3EE' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-container">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6">Cihaz Dağılımı</h3>
          {deviceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={deviceData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={5} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F8FAFC' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-slate-500">Henüz veri yok</div>
          )}
        </div>

        <div className="chart-container">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6">Tarayıcı Dağılımı</h3>
          {browserData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={browserData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" stroke="#64748B" width={80} tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F8FAFC' }} />
                <Bar dataKey="value" fill="#06B6D4" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-slate-500">Henüz veri yok</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-5 h-5 text-cyan-400" />
            <h3 className="text-base sm:text-lg font-semibold text-white">Ülkeler</h3>
          </div>
          {countryData.length > 0 && !(countryData.length === 1 && countryData[0].name === 'Bilinmiyor') ? (
            <div className="space-y-2.5">
              {countryData.slice(0, 8).map((country, index) => {
                const total = countryData.reduce((a, b) => a + b.value, 0);
                const pct = ((country.value / total) * 100).toFixed(1);
                const colors = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316'];
                return (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[index % colors.length] }} />
                    <span className="text-slate-300 text-sm flex-1 truncate">{country.name || 'Bilinmiyor'}</span>
                    <span className="text-white font-medium text-sm">{country.value}</span>
                    <div className="w-16 bg-white/5 rounded-full h-1.5 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: colors[index % colors.length] }} />
                    </div>
                    <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Henüz veri yok</p>
          )}
        </div>

        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <img src="/favicon.png" alt="Link" className="w-5 h-5" />
            <h3 className="text-base sm:text-lg font-semibold text-white">Kaynaklar</h3>
          </div>
          {referrerData.length > 0 ? (
            <div className="space-y-3">
              {referrerData.slice(0, 5).map((ref, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-slate-300 truncate max-w-[120px] sm:max-w-[150px] text-sm">{ref.name}</span>
                  <span className="text-cyan-400 font-medium text-sm">{ref.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Henüz veri yok</p>
          )}
        </div>

        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Monitor className="w-5 h-5 text-cyan-400" />
            <h3 className="text-base sm:text-lg font-semibold text-white">İşletim Sistemi</h3>
          </div>
          {Object.keys(safeOsStats).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(safeOsStats).slice(0, 5).map(([name, value], index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">{name}</span>
                  <span className="text-cyan-400 font-medium text-sm">{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">Henüz veri yok</p>
          )}
        </div>
      </div>
    </>
  );

  const renderDailyTab = () => {
    const sourceSummary = safeSources
      .map(s => `${SOURCE_LABELS[s] || s}: ${safeSourceTotals[s] || 0}`)
      .join(' | ');

    return (
      <div className="space-y-6">
        <div className="chart-container">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Tıklama Kaynağı</h3>
          <p className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6 break-words">{sourceSummary}</p>
          
          {safeDailyBySource.length > 0 && safeSources.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={safeDailyBySource}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="date" 
                  stroke="#64748B"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => {
                    try { return new Date(value).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }); } catch { return value; }
                  }}
                />
                <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomSourceTooltip />} />
                <Legend 
                  formatter={(value) => SOURCE_LABELS[value] || value}
                  wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                />
                {safeSources.map((source) => (
                  <Bar 
                    key={source} 
                    dataKey={source} 
                    stackId="a" 
                    fill={SOURCE_COLORS[source] || '#8B5CF6'}
                    name={source}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-slate-500">Henüz veri yok</div>
          )}
        </div>

        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Platform Bazlı Toplam</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(safeSourceTotals)
              .sort(([,a], [,b]) => b - a)
              .map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 p-2 sm:p-3 rounded-xl bg-white/5">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[key] || '#8B5CF6' }} />
                  <span className="text-slate-300 text-xs sm:text-sm truncate">{SOURCE_LABELS[key] || key}</span>
                  <span className="text-white font-semibold text-xs sm:text-sm ml-auto">{value}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  };

  const renderRedirectsTab = () => {
    const referrersByDomain = {};
    Object.entries(safeReferrers).forEach(([ref, count]) => {
      if (ref === 'Doğrudan') {
        referrersByDomain['Doğrudan'] = (referrersByDomain['Doğrudan'] || 0) + count;
        return;
      }
      try {
        const url = new URL(ref);
        const host = url.hostname.replace(/^www\./, '');
        referrersByDomain[host] = (referrersByDomain[host] || 0) + count;
      } catch {
        referrersByDomain[ref] = (referrersByDomain[ref] || 0) + count;
      }
    });

    const KEYWORD_COLOR_MAP = [
      { keywords: ['facebook', 'fb.com', 'fbcdn'], source: 'facebook' },
      { keywords: ['instagram'], source: 'instagram' },
      { keywords: ['twitter', 'x.com', 't.co'], source: 'twitter' },
      { keywords: ['linkedin', 'lnkd.in'], source: 'linkedin' },
      { keywords: ['youtube', 'youtu.be'], source: 'youtube' },
      { keywords: ['tiktok'], source: 'tiktok' },
      { keywords: ['reddit'], source: 'reddit' },
      { keywords: ['pinterest', 'pin.it'], source: 'pinterest' },
      { keywords: ['telegram', 't.me'], source: 'telegram' },
      { keywords: ['whatsapp', 'wa.me'], source: 'whatsapp' },
      { keywords: ['google'], source: 'google' },
    ];

    const domainData = Object.entries(referrersByDomain)
      .map(([name, value]) => {
        const nameLower = name.toLowerCase();
        let color = '#8B5CF6';
        if (name === 'Doğrudan') {
          color = SOURCE_COLORS.dogrudan;
        } else {
          for (const { keywords, source } of KEYWORD_COLOR_MAP) {
            if (keywords.some(kw => nameLower.includes(kw))) {
              color = SOURCE_COLORS[source] || '#8B5CF6';
              break;
            }
          }
        }
        return { name, value, fill: color };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);

    return (
      <div className="space-y-6">
        <div className="chart-container">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6">Yönlendirmeler / Diğer Kaynaklar</h3>
          {domainData.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(250, domainData.length * 45)}>
              <BarChart data={domainData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11 }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  stroke="#64748B" 
                  width={120} 
                  tick={{ fontSize: 11 }}
                />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#F8FAFC' }}
                  formatter={(value, name) => [value, 'Tıklama']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {domainData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-slate-500">Henüz veri yok</div>
          )}
        </div>

        {(source_totals || {}).dogrudan > 0 && (
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span><strong className="text-slate-400">Doğrudan:</strong> Referrer bilgisi olmayan tıklamalar — tarayıcıya doğrudan URL girişi, yer imleri, mesaj uygulamaları (SMS, e-posta), bazı mobil uygulamalar ve gizli pencere tıklamaları bu kategoriye dahildir.</span>
          </p>
        )}

        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Tüm Yönlendirme Kaynakları</h3>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {domainData.map((item, index) => (
              <div key={index} className="flex items-center gap-3 p-2 sm:p-3 rounded-xl bg-white/5">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: item.fill }} />
                <span className="text-slate-300 text-xs sm:text-sm truncate flex-1">{item.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-white font-semibold text-xs sm:text-sm">{item.value}</span>
                  <span className="text-slate-500 text-xs">
                    ({total_clicks > 0 ? ((item.value / total_clicks) * 100).toFixed(1) : 0}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B]">
      <header className="sticky top-0 z-30 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/panel" className="p-1.5 sm:p-2 text-slate-400 hover:text-white flex-shrink-0" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-semibold text-white truncate">{link.title || link.short_code}</h1>
                <div className="flex items-center gap-1 sm:gap-2">
                  <code className="text-cyan-400 text-xs sm:text-sm font-mono truncate">{getShortUrl(link.short_code)}</code>
                  <button onClick={() => copyToClipboard(getShortUrl(link.short_code))} className="text-slate-500 hover:text-cyan-400 flex-shrink-0" data-testid="copy-link-btn">
                    <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <Button variant="ghost" size="icon" onClick={() => setEditDialogOpen(true)} className="text-slate-400 hover:text-white h-8 w-8 sm:h-9 sm:w-9" data-testid="edit-link-btn">
                <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => window.open(getShortUrl(link.short_code), '_blank')} className="text-slate-400 hover:text-white h-8 w-8 sm:h-9 sm:w-9">
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleDelete} className="text-red-400 hover:text-red-300 h-8 w-8 sm:h-9 sm:w-9" data-testid="delete-link-btn">
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="stat-card" data-testid="stat-total-clicks">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <MousePointerClick className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Toplam Tıklama</p>
            <p className="text-xl sm:text-2xl font-bold text-white">{total_clicks || 0}</p>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Durum</p>
            <p className={`text-sm sm:text-lg font-semibold ${link.is_active !== false ? 'text-emerald-400' : 'text-red-400'}`}>
              {link.is_active !== false ? 'Aktif' : 'Pasif'}
            </p>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Benzersiz Cihaz</p>
            <p className="text-xl sm:text-2xl font-bold text-white">{Object.keys(safeDevices).length}</p>
          </div>

          <div className="stat-card">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
              </div>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 mb-1">Filtrelenen Bot</p>
            <p className="text-xl sm:text-2xl font-bold text-white">{bot_clicks_filtered || 0}</p>
          </div>
        </div>

        <div className="flex gap-1 sm:gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'genel' && renderGeneralTab()}
        {activeTab === 'gunluk' && renderDailyTab()}
        {activeTab === 'yonlendirmeler' && renderRedirectsTab()}

        <div className="mt-6 sm:mt-8 bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Link Bilgileri</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs sm:text-sm text-slate-500 mb-1">Orijinal URL</p>
              <p className="text-slate-300 break-all text-xs sm:text-sm">{link.original_url}</p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-slate-500 mb-1">Kısa URL</p>
              <p className="text-cyan-400 font-mono text-xs sm:text-sm">{getShortUrl(link.short_code)}</p>
            </div>
            {link.expires_at && (
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Bitiş Tarihi</p>
                <p className="text-slate-300 text-xs sm:text-sm">{new Date(link.expires_at).toLocaleString('tr-TR')}</p>
              </div>
            )}
            <div>
              <p className="text-xs sm:text-sm text-slate-500 mb-1">Oluşturulma</p>
              <p className="text-slate-300 text-xs sm:text-sm">{link.created_at ? new Date(link.created_at).toLocaleDateString('tr-TR') : '-'}</p>
            </div>
          </div>
        </div>
      </main>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Link Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Başlık</Label>
              <Input type="text" value={editData.title} onChange={(e) => setEditData({...editData, title: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Aktif</Label>
              <button type="button" onClick={() => setEditData({...editData, is_active: !editData.is_active})}
                className={`w-12 h-6 rounded-full transition-colors duration-200 ${editData.is_active ? 'bg-cyan-600' : 'bg-slate-600'}`}>
                <div className={`w-5 h-5 rounded-full bg-white transform transition-transform duration-200 ${editData.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-400">İptal</Button>
              <Button type="submit" disabled={editLoading} className="bg-cyan-600 hover:bg-cyan-500">
                {editLoading ? <div className="spinner w-4 h-4" /> : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LinkDetails;
